import type { CSSProperties } from 'react';
import type { TableCell, TableCellStyle } from './tableTypes';

/** Background + weight per cell style. Applied inline so a stylesheet can't lose to it. */
export const TABLE_CELL_STYLE: Record<
  TableCellStyle,
  { backgroundColor: string; fontWeight: number }
> = {
  header: { backgroundColor: '#eef0ec', fontWeight: 700 },
  subHeader: { backgroundColor: '#f6f7f4', fontWeight: 600 },
  normal: { backgroundColor: 'transparent', fontWeight: 400 },
};

/** Effective style of a rendered cell — an unset row-0 cell still reads as a header. */
export function resolveRenderedCellStyle(
  cell: Pick<TableCell, 'style'>,
  isHeaderRow: boolean,
): TableCellStyle {
  return cell.style ?? (isHeaderRow ? 'header' : 'normal');
}

/** Class list for a rendered table cell — its style tint plus the sticky first column. */
export function tableCellClassName(
  cell: TableCell,
  isHeaderRow: boolean,
  isFirstColumn: boolean,
): string | undefined {
  const style = resolveRenderedCellStyle(cell, isHeaderRow);
  return (
    [
      isFirstColumn && 'first-column',
      style === 'header' && 'cell-header',
      style === 'subHeader' && 'cell-subheader',
    ]
      .filter(Boolean)
      .join(' ') || undefined
  );
}

/** `className` + inline `style` for a rendered `<td>` / `<th>`. */
export function tableCellRenderProps(
  cell: TableCell,
  isHeaderRow: boolean,
  isFirstColumn: boolean,
): { className?: string; style?: CSSProperties } {
  const kind = resolveRenderedCellStyle(cell, isHeaderRow);
  const className = tableCellClassName(cell, isHeaderRow, isFirstColumn);
  if (kind === 'normal') return { className };
  const { backgroundColor, fontWeight } = TABLE_CELL_STYLE[kind];
  return { className, style: { backgroundColor, fontWeight } };
}
